import React, { useEffect } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { newcomerService } from '../../services/newcomer.service';
import type { Newcomer, NewcomerFormData } from '../../types/newcomer';
import { usePermissions } from '../../hooks/usePermissions';
import { FaLock } from 'react-icons/fa';

interface NewcomerFormProps {
  editingNewcomer?: Newcomer | null;
  onSuccess?: () => void;
  readOnly?: boolean;
}

export const NewcomerForm: React.FC<NewcomerFormProps> = ({ editingNewcomer, onSuccess, readOnly = false }) => {
  const { canWrite } = usePermissions();
  const isReadOnly = readOnly || !canWrite('newcomers');
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, watch } = useForm<NewcomerFormData>();
  const visitPurposeValue = watch('visitPurpose');

  useEffect(() => {
    if (editingNewcomer) {
      reset(editingNewcomer);
    } else {
      reset({ gender: 'male' });
    }
  }, [editingNewcomer, reset]);

  const onSubmit: SubmitHandler<NewcomerFormData> = async (data) => {
    try {
      if (editingNewcomer) {
        await newcomerService.updateNewcomer(editingNewcomer.id, data);
        alert('새가족 정보가 수정되었습니다.');
      } else {
        await newcomerService.addNewcomer(data);
        alert('새가족이 등록되었습니다.');
      }
      onSuccess?.();
    } catch (error) {
      alert('처리 중 오류가 발생했습니다.');
      console.error(error);
    }
  };

  return (
    <div className="bg-white rounded-lg space-y-6">
      {/* 읽기 전용 모드 알림 */}
      {isReadOnly && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <div className="flex items-center space-x-2">
            <FaLock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              읽기 전용 모드입니다. 정보를 수정할 권한이 없습니다.
            </span>
          </div>
        </div>
      )}
      
      <form onSubmit={isReadOnly ? undefined : handleSubmit(onSubmit)} className="bg-white rounded-lg space-y-6">
      <div className="p-6 border border-gray-300 rounded-md shadow-md space-y-6">
        {/* --- 개인 정보 섹션 --- */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 border-t pt-6">
          <div className="col-span-1 md:col-span-5">
            <label htmlFor="name" className="block text-base font-normal text-gray-600">이름 <span className="text-red-500">*</span></label>
            <input id="name" {...register('name', { required: '이름은 필수 항목입니다.' })} lang="ko" inputMode="text" disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>
          <div className="col-span-1 md:col-span-3">
            <label htmlFor="gender" className="block text-base font-normal text-gray-600">성별</label>
            <select id="gender" {...register('gender')} disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600 font-normal ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}>
              <option value="male">남성</option>
              <option value="female">여성</option>
              <option value="other">기타</option>
            </select>
          </div>
          <div className="col-span-1 md:col-span-4">
            <label htmlFor="dateOfBirth" className="block text-base font-normal text-gray-600">생년월일</label>
            <input type="date" id="dateOfBirth" {...register('dateOfBirth')} lang="ko" disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="phoneNumber" className="block text-base font-normal text-gray-600">전화번호 <span className="text-red-500">*</span></label>
            <input id="phoneNumber" {...register('phoneNumber', { required: '연락처는 필수 항목입니다.' })} lang="ko" inputMode="tel" disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} />
            {errors.phoneNumber && <p className="mt-1 text-sm text-red-600">{errors.phoneNumber.message}</p>}
          </div>
          <div>
            <label htmlFor="email" className="block text-base font-normal text-gray-600">이메일</label>
            <input type="email" id="email" {...register('email')} lang="ko" inputMode="email" disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} />
          </div>
          <div>
            <label htmlFor="kakaoId" className="block text-base font-normal text-gray-600">카카오톡 ID</label>
            <input id="kakaoId" {...register('kakaoId')} lang="ko" inputMode="text" disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6">
          <div>
            <label htmlFor="visitPurpose" className="block text-base font-normal text-gray-600">방문목적 <span className="text-red-500">*</span></label>
            <select id="visitPurpose" {...register('visitPurpose', { required: '방문목적은 필수 항목입니다.' })} disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600 font-normal ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}>
              <option value="">선택...</option>
              <option value="work">직장</option><option value="business">사업</option><option value="study">유학</option><option value="travel">여행</option><option value="other">기타</option>
            </select>
          </div>
          
          {visitPurposeValue === 'other' && (
            <div className="col-span-1 md:col-span-2">
              <label htmlFor="visitPurposeDetail" className="block text-base font-normal text-gray-600">방문목적 (직접입력)</label>
              <input id="visitPurposeDetail" {...register('visitPurposeDetail', { required: '기타 목적을 입력해주세요.' })} lang="ko" inputMode="text" disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} />
              {errors.visitPurposeDetail && <p className="mt-1 text-sm text-red-600">{errors.visitPurposeDetail.message}</p>}
            </div>
          )}

          <div>
            <label htmlFor="guide" className="block text-base font-normal text-gray-600">인도자</label>
            <input id="guide" {...register('guide')} lang="ko" inputMode="text" disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} />
          </div>
          <div>
            <label htmlFor="stayDuration" className="block text-base font-normal text-gray-600">체류기간</label>
            <input id="stayDuration" {...register('stayDuration')} lang="ko" inputMode="text" disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} />
          </div>
          <div>
            <label htmlFor="hasFaith" className="block text-base font-normal text-gray-600">신앙유무 <span className="text-red-500">*</span></label>
            <select id="hasFaith" {...register('hasFaith', { required: '신앙유무는 필수 항목입니다.' })} disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600 font-normal ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}>
              <option value="">선택...</option><option value="yes">유</option><option value="no">무</option>
            </select>
          </div>
        </div>
      </div>
      {!isReadOnly && (
        <div className="flex justify-end pt-4">
          <button type="submit" disabled={isSubmitting} className="px-4 py-1.5 bg-btn-green-bg hover:bg-btn-green-hover text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out disabled:opacity-50">
            {isSubmitting ? '저장 중...' : (editingNewcomer ? '정보 수정' : '새가족 등록')}
          </button>
        </div>
      )}
      </form>
    </div>
  );
};
